import React from "react";
import { getConsolidationGroupDisplayName } from "@common/helpers/entity";
import {
    ACCOUNTING_REPORT_LABELS,
    AccountingReportType,
} from "@common/types/reports/report-type";
import { ConsolidationGroupResponse } from "@common/schemas/domains/accounting/consolidation.schema";
import { Entity } from "@common/types/entity";
import { useLedgers } from "@api/ledger.api";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { buildEliminationDetailUrl } from "@components/accounting/buildEliminationDetailUrl";
import { Button } from "@design-system/Button/Button";
import { ArrowCornerIcon } from "@icons";
import {
    buildConsolidatedReportUrl,
    canOpenConsolidatedReport,
    resolveConsolidatedReportLedgerId,
} from "../ownershipConsolidationReports.helpers";
import {
    UpdateGroupConfigCallbacks,
    UpdateGroupConfigParams,
} from "./ConsolidationBasisSelector";
import { EntityPaneConsolidationSection } from "./EntityPaneConsolidationSection";
import { PaneSection } from "./PaneSection";
import styles from "./Pane.module.scss";

interface ConsolidationGroupPaneProps {
    workspaceId: string;
    group: ConsolidationGroupResponse;
    entitiesByUuid: Map<string, Entity>;
    isUpdatingGroupConfig: boolean;
    onUpdateGroupConfig: (
        params: UpdateGroupConfigParams,
        callbacks?: UpdateGroupConfigCallbacks,
    ) => void;
}

interface ReportLink {
    key: string;
    label: string;
    url: string;
}

/**
 * The consolidation-only side panel shown when the chip is selected — keeps
 * ledger basis/consolidation-method data out of the parent entity's own pane,
 * since a consolidation group isn't itself an entity. Unconsolidate lives in
 * the command island.
 */
export const ConsolidationGroupPane: React.FC<ConsolidationGroupPaneProps> = ({
    workspaceId,
    group,
    entitiesByUuid,
    isUpdatingGroupConfig,
    onUpdateGroupConfig,
}) => {
    const navigate = useStateAwareNavigate();
    const parentEntity = entitiesByUuid.get(group.parentEntityUuid);
    const { data: ledgers } = useLedgers(parentEntity?.id);
    const ledgerId = resolveConsolidatedReportLedgerId({ group, ledgers });

    const reportLinks: ReportLink[] = [
        {
            key: "elimination-detail",
            label: ACCOUNTING_REPORT_LABELS[
                AccountingReportType.ELIMINATION_DETAIL
            ],
            url: buildEliminationDetailUrl({
                workspaceId,
                consolidationGroupId: group.id,
            }),
        },
    ];
    if (
        parentEntity &&
        ledgerId &&
        canOpenConsolidatedReport({ group, parentEntity, ledgerId })
    ) {
        reportLinks.push(
            {
                key: "consolidated-pl",
                label: `${
                    ACCOUNTING_REPORT_LABELS[AccountingReportType.PROFIT_LOSS]
                } (Consolidated)`,
                url: buildConsolidatedReportUrl({
                    report: "profit-loss",
                    parentEntity,
                    ledgerId,
                    group,
                }),
            },
            {
                key: "consolidated-bs",
                label: `${
                    ACCOUNTING_REPORT_LABELS[AccountingReportType.BALANCE_SHEET]
                } (Consolidated)`,
                url: buildConsolidatedReportUrl({
                    report: "balance-sheet",
                    parentEntity,
                    ledgerId,
                    group,
                }),
            },
        );
    }

    return (
        <div className={styles.pane}>
            <PaneSection title="Details" defaultExpanded>
                <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Name</span>
                    <span className={styles.detailValue}>
                        {getConsolidationGroupDisplayName({
                            groupName: group.name,
                            parentEntityName: parentEntity?.name,
                        })}
                    </span>
                </div>
                <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Parent entity</span>
                    <span className={styles.detailValue}>
                        {parentEntity?.name ?? "Unknown entity"}
                    </span>
                </div>
            </PaneSection>
            <EntityPaneConsolidationSection
                workspaceId={workspaceId}
                group={group}
                entitiesByUuid={entitiesByUuid}
                isUpdatingGroupConfig={isUpdatingGroupConfig}
                onUpdateGroupConfig={onUpdateGroupConfig}
            />
            <PaneSection title="Reporting" defaultExpanded>
                <div className={styles.reportLinks}>
                    {reportLinks.map((link) => (
                        <Button
                            key={link.key}
                            variant="secondary"
                            size="sm"
                            rightIcon={<ArrowCornerIcon />}
                            onClick={() => navigate(link.url)}
                        >
                            {link.label}
                        </Button>
                    ))}
                </div>
            </PaneSection>
        </div>
    );
};
